import { useState } from 'react';
import { closeModal, openToast } from 'utils/localDispatcher';
import { createChatStoreOrder } from 'apis/CommerceGraphql/chatOrder';
import { ChatStoreProductInput, MoneyCurrency } from '__generated__/commerce';
import type { GetChatOrderEligibilityQuery } from '__generated__/commerce';
import { numberInputComma } from 'utils/snippet';
import { CloseIcon, ILowercaseSerifCircleIcon } from 'utils/SVGIcons';
import { ChatOrderProductCandidatesModalStyle } from './ChatOrderProductCandidatesModalStyle';
import useChatOrderSelection from './hooks/useChatOrderSelection';
import ProductCard from './ProductCard';

export type ChatOrderProduct = NonNullable<
  GetChatOrderEligibilityQuery['store']
>['chatOrderProducts'][number];

interface ChatOrderProductCandidatesModalProps {
  buyerAccountId: string;
  storeId: string;
  channelId: string;
  products: ChatOrderProduct[];
  onDirectInput: () => void;
}

const ChatOrderProductCandidatesModal = ({
  buyerAccountId,
  storeId,
  channelId,
  products,
  onDirectInput,
}: ChatOrderProductCandidatesModalProps) => {
  const { state, select, increaseQuantity, decreaseQuantity, toggleDeliveryFee, selectDeliveryFeeOption, isSubmitDisabled } =
    useChatOrderSelection();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedProduct = products.find((p) => p._id === state.productId) ?? null;

  const handleProductSelect = (product: ChatOrderProduct, optionId?: string) => {
    const option = optionId ? product.options.find((o) => o._id === optionId) : null;
    const price = option?.price?.amount ?? product.price?.amount ?? 0;
    select({
      productId: product._id,
      optionId: optionId ?? null,
      price,
    });
  };

  const handleOptionSelect = (product: ChatOrderProduct, optionId: string) => {
    handleProductSelect(product, optionId);
  };

  const handleSubmit = async () => {
    if (!selectedProduct || isSubmitDisabled || isSubmitting) return;

    setIsSubmitting(true);

    const selectedOption = selectedProduct.options.find((o) => o._id === state.optionId);
    const productName = selectedOption
      ? `${selectedProduct.name} ${selectedOption.name}`
      : selectedProduct.name;

    const deliveryFeeOptionIndex = Math.min(
      state.selectedDeliveryFeeOptionIndex,
      selectedProduct.deliveryFeeOptions.length - 1
    );
    const deliveryFee = state.includeDeliveryFee && deliveryFeeOptionIndex >= 0
      ? selectedProduct.deliveryFeeOptions[deliveryFeeOptionIndex].fee.amount
      : 0;
    const pictureId = selectedProduct.thumbnail ? selectedProduct.thumbnail._id : null;

    const productInput: ChatStoreProductInput = {
      productName,
      price: {
        amount: state.price,
        currency: MoneyCurrency.Won,
      },
      quantity: state.quantity,
      deliveryFee: deliveryFee ? {
        amount: deliveryFee,
        currency: MoneyCurrency.Won,
      } : null,
      pictureId,
    };

    try {
      const result = await createChatStoreOrder({
        buyerAccountId,
        storeId,
        products: [productInput],
        metadata: { chat: { channelId } },
      });

      if (result.error) {
        setIsSubmitting(false);
        switch (result.error.__typename) {
          case 'ChatOrderInvalidOrder':
            alert('결제 요청에 실패했어요. 입력을 다시 확인해주세요.');
            return;
          case 'ChatOrderStoreNotFound':
            alert('결제 요청에 실패했어요. 가게를 찾을 수 없어요.');
            return;
          case 'ChatOrderStoreNotOwned':
            alert('결제 요청에 실패했어요. 가게 소유권이 없어요.');
            return;
          default:
            alert('결제 요청에 실패했어요. 잠시 후 다시 시도해주세요.');
            return;
        }
      }

      openToast('결제 요청이 전송되었어요.');
      closeModal();
    } catch {
      setIsSubmitting(false);
      alert('결제 요청에 실패했어요. 잠시 후 다시 시도해주세요.');
    }
  };

  const formatPrice = (amount: number) => `${numberInputComma(amount)}원`;

  const totalAmount = (() => {
    if (!selectedProduct || state.quantity <= 0) return 0;
    const deliveryFeeOptionIndex = Math.min(
      state.selectedDeliveryFeeOptionIndex,
      selectedProduct.deliveryFeeOptions.length - 1
    );
    const deliveryFee = state.includeDeliveryFee && deliveryFeeOptionIndex >= 0
      ? selectedProduct.deliveryFeeOptions[deliveryFeeOptionIndex].fee.amount
      : 0;
    return state.price * state.quantity + deliveryFee;
  })();

  return (
    <ChatOrderProductCandidatesModalStyle>
      <button type="button" className="close-button" onClick={closeModal}>
        <CloseIcon size={24} />
      </button>

      <div className="title">결제요청</div>

      <div className="info-banner">
        <div className="info-icon">
          <ILowercaseSerifCircleIcon />
        </div>
        <div className="info-text">
          상품을 선택하고 결제를 요청하면, 채팅방에 주문서가 생성돼요.
          목록에 없는 상품은 &apos;직접 입력하기&apos;로 추가할 수 있어요.
        </div>
      </div>

      <div className="product-list">
        {products.map((product) => (
          <ProductCard
            key={product._id}
            product={product}
            isSelected={state.productId === product._id}
            state={state}
            formatPrice={formatPrice}
            onProductSelect={handleProductSelect}
            onOptionSelect={handleOptionSelect}
            onIncreaseQuantity={increaseQuantity}
            onDecreaseQuantity={decreaseQuantity}
            onToggleDeliveryFee={toggleDeliveryFee}
            onSelectDeliveryFeeOption={selectDeliveryFeeOption}
          />
        ))}
      </div>

      <div className="button-group">
        <button type="button" className="direct-input-button" onClick={onDirectInput}>
          직접 입력하기
        </button>
        <button
          type="button"
          className="submit-button"
          onClick={handleSubmit}
          disabled={isSubmitDisabled || isSubmitting}
        >
          {isSubmitting ? '요청 중...' : totalAmount > 0 ? `${formatPrice(totalAmount)} 결제 요청하기` : '결제 요청하기'}
        </button>
      </div>
    </ChatOrderProductCandidatesModalStyle>
  );
};

export default ChatOrderProductCandidatesModal;
